<template>
  <section>
    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(saveRole)">
        <div class="modal-card" style="width: 900px; max-width: 100%;">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ modalTitle }}</p>
            <button type="button" class="delete" @click="$emit('close')" />
          </header>

          <section class="modal-card-body">
            <!-- Errores de validación -->
            <div class="mb-3" v-if="Object.keys(errores_validacion).length !== 0">
              <b-message type="is-danger" has-icon>
                <div v-for="(item, index) in errores_validacion" :key="index">{{ item[0] }}</div>
              </b-message>
            </div>

            <!-- PASO 1: Búsqueda de persona (solo en creación) -->
            <template v-if="isCreate">
              <div class="box has-background-light mb-4">
                <p class="has-text-weight-semibold mb-2"><b-icon pack="fas" icon="search" size="is-small" /> Buscar persona en el sistema</p>
                <b-field>
                  <p class="control has-icons-left is-expanded">
                    <input v-model="busqueda" class="input" type="text" placeholder="Nombre, apellido o RUT..." @keyup.enter="search(busqueda)">
                    <span class="icon is-left"><i class="fas fa-search"></i></span>
                  </p>
                  <div class="control">
                    <b-button @click.prevent="search(busqueda)" class="is-primary">Buscar</b-button>
                  </div>
                </b-field>

                <b-table v-if="people.length > 0"
                    :data="people"
                    :paginated="true"
                    :selected.sync="persona_seleccionada"
                    per-page="5"
                    striped
                    hoverable
                    :current-page.sync="currentPage"
                    pagination-position="bottom"
                    size="is-small">
                  <b-table-column field="name" label="Nombre" sortable v-slot="props">{{ props.row.name }}</b-table-column>
                  <b-table-column field="last_name" label="Apellido" sortable v-slot="props">{{ props.row.last_name }}</b-table-column>
                  <b-table-column field="second_surname" label="2° Apellido" v-slot="props">{{ props.row.second_surname }}</b-table-column>
                  <b-table-column field="corporate_mail" label="Correo" v-slot="props">{{ props.row.corporate_mail }}</b-table-column>
                </b-table>

                <div class="mt-2" v-if="persona_seleccionada">
                  <b-tag type="is-success" closable @close="clearPerson">
                    Seleccionado: {{ persona_seleccionada.name }} {{ persona_seleccionada.last_name }}
                  </b-tag>
                </div>
              </div>
            </template>

            <!-- Aviso para nombramiento académico pendiente -->
            <div v-if="isNombramiento" class="notification is-info is-light mb-3">
              <b-icon pack="fas" icon="info-circle" size="is-small" />
              Se asignará el rol al <strong>nombramiento existente</strong> y se creará el usuario de acceso si no lo tiene.
              La unidad y fechas provienen del nombramiento y no se modifican.
            </div>

            <!-- Datos de la persona (solo lectura en edición) -->
            <div class="columns" v-if="!isCreate || persona_seleccionada">
              <div class="column">
                <b-field label="Nombre">
                  <b-input :value="person.name" disabled />
                </b-field>
              </div>
              <div class="column">
                <b-field label="Apellido">
                  <b-input :value="person.last_name" disabled />
                </b-field>
              </div>
              <div class="column">
                <b-field label="Correo corporativo">
                  <b-input :value="person.corporate_mail" disabled />
                </b-field>
              </div>
            </div>

            <!-- Formulario del rol -->
            <template v-if="!isCreate || persona_seleccionada">
              <div class="columns">
                <div class="column">
                  <b-field label="Rol *">
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <multiselect
                          v-model="person_unit.internal_position"
                          :options="internal_positions.map(t => t.id)"
                          :custom-label="opt => { const f = internal_positions.find(x => x.id === opt); return f ? f.name : ''; }"
                          placeholder="Seleccione un rol"
                          selectLabel="Presione para seleccionar"
                          selectedLabel="Seleccionado"
                          deselectLabel="No se puede deseleccionar"
                          :allow-empty="false">
                        <template v-slot:noOptions>No existen datos</template>
                        <span slot="noResult">No se encontraron elementos.</span>
                      </multiselect>
                      <span class="validation-alert">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </b-field>
                </div>
                <div class="column">
                  <b-field label="Unidad *">
                    <ValidationProvider :rules="isNombramiento ? '' : 'required'" v-slot="{ errors }">
                      <multiselect
                          v-model="person_unit.unit_id"
                          :options="units.map(t => t.id)"
                          :custom-label="opt => { const f = units.find(x => x.id === opt); return f ? f.name : ''; }"
                          placeholder="Seleccione una unidad"
                          selectLabel="Presione para seleccionar"
                          selectedLabel="Seleccionado"
                          deselectLabel="No se puede deseleccionar"
                          :disabled="isNombramiento"
                          :allow-empty="false">
                        <template v-slot:noOptions>No existen datos</template>
                        <span slot="noResult">No se encontraron elementos.</span>
                      </multiselect>
                      <span class="validation-alert">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </b-field>
                </div>
              </div>

              <div class="columns">
                <div class="column">
                  <b-field label="Descripción *">
                    <ValidationProvider :rules="isNombramiento ? '' : 'required'" v-slot="{ errors }">
                      <b-input v-model="person_unit.description" type="text" maxlength="200" :disabled="isNombramiento" />
                      <span class="validation-alert">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </b-field>
                </div>
              </div>

              <div class="columns">
                <div class="column">
                  <b-field label="Fecha Desde *">
                    <ValidationProvider :rules="isNombramiento ? '' : 'required'" v-slot="{ errors }">
                      <b-datepicker v-model="person_unit.start_date" icon="calendar-alt" trap-focus locale="es-ES" editable placeholder="dd/mm/aaaa" :disabled="isNombramiento" />
                      <span class="validation-alert">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </b-field>
                </div>
                <div class="column">
                  <b-field label="Fecha Hasta">
                    <b-datepicker v-model="person_unit.end_date" icon="calendar-alt" trap-focus locale="es-ES" editable placeholder="dd/mm/aaaa" :disabled="isNombramiento" />
                  </b-field>
                </div>
              </div>
            </template>
          </section>

          <footer class="modal-card-foot actions">
            <div class="field is-grouped is-pulled-right">
              <button class="button" type="button" @click="$emit('close')">Cerrar</button>
              <button class="button is-primary" type="submit"
                      :disabled="isCreate && !persona_seleccionada">Guardar</button>
            </div>
          </footer>
        </div>
      </form>
    </ValidationObserver>
    <b-loading :is-full-page="true" v-model="isLoading" :can-cancel="false"></b-loading>
  </section>
</template>

<script>
import axios from "axios";

export default {
  name: "modal_add_roles_fing",
  props: ["id", "queryType", "personId"],
  data() {
    return {
      isLoading: false,
      person_unit: {},
      person: { id: null, name: null, last_name: null, second_surname: null, corporate_mail: null,
                run_attributes: { rut: null, dv: null } },
      people: [],
      units: [],
      internal_positions: [],
      errores_validacion: {},
      persona_seleccionada: null,
      busqueda: "",
      currentPage: 1,
      isNombramiento: false,
    };
  },
  computed: {
    isCreate() {
      return this.queryType === "create";
    },
    modalTitle() {
      if (this.isCreate) return "Asignar Rol";
      if (this.isNombramiento) return "Asignar Rol a Nombramiento Académico";
      return "Editar Rol";
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      const loading = this.$buefy.loading.open();
      if (this.isCreate) {
        axios.get("/admin/person_units/create_roles_fing.json")
            .then(res => {
              this.internal_positions = res.data.internal_positions_filtered;
              this.units = res.data.units_all;
              loading.close();
            }).catch(err => { console.log(err); loading.close(); });
      } else {
        axios.get(`/admin/person_units/${this.id}/edit_roles_fing.json`)
            .then(res => {
              this.isNombramiento = res.data.pu_relation_type_id === 1;
              this.person_unit = {
                id: res.data.id,
                internal_position: res.data.internal_position ? res.data.internal_position.id : null,
                unit_id: res.data.unit ? res.data.unit.id : null,
                description: res.data.description,
                start_date: res.data.start_date ? new Date(this.isoDate(res.data.start_date)) : null,
                end_date: res.data.end_date ? new Date(this.isoDate(res.data.end_date)) : null,
              };
              this.person = {
                id: res.data.person.id,
                name: res.data.person.name,
                last_name: res.data.person.last_name,
                second_surname: res.data.person.second_surname,
                corporate_mail: res.data.person.corporate_mail,
                run_attributes: res.data.run_attributes
              };
              this.internal_positions = res.data.internal_positions_filtered;
              this.units = res.data.units_all;
              // Nombramiento académico pendiente sin rol: preseleccionar ACADÉMICO
              if (this.isNombramiento && !this.person_unit.internal_position) {
                const academicoIp = this.internal_positions.find(x => x.name === "ACADEMICO");
                if (academicoIp) this.$set(this.person_unit, 'internal_position', academicoIp.id);
              }
              loading.close();
            }).catch(err => { console.log(err); loading.close(); });
      }
    },
    isoDate(date) {
      return date.replace(/-/g, "/");
    },
    search(busqueda) {
      if (!busqueda) return;
      const loading = this.$buefy.loading.open();
      axios.get(`/people/${busqueda}/search_all_people.json`)
          .then(res => { this.people = res.data; loading.close(); })
          .catch(err => { console.log(err); loading.close(); });
    },
    clearPerson() {
      this.persona_seleccionada = null;
      this.person = { id: null, name: null, last_name: null, second_surname: null, corporate_mail: null,
                      run_attributes: { rut: null, dv: null } };
    },
    saveRole() {
      const loading = this.$buefy.loading.open();
      const personPayload = this.isCreate ? {
        id: this.persona_seleccionada.id,
        name: this.persona_seleccionada.name,
        last_name: this.persona_seleccionada.last_name,
        second_surname: this.persona_seleccionada.second_surname,
        corporate_mail: this.persona_seleccionada.corporate_mail,
        run_attributes: this.persona_seleccionada.run_attributes
      } : this.person;

      if (this.isCreate) {
        axios.post("/admin/person_units/new_role_fing.json", {
          person_unit: this.person_unit,
          pu_relation_type_id: 2,
          person: personPayload
        }).then(() => {
          this.errores_validacion = {};
          this.$buefy.dialog.alert({ message: "El rol fue asignado con éxito.", confirmText: "Ok" });
          loading.close();
          this.$emit("close");
        }).catch(err => {
          this.errores_validacion = err.response.data;
          this.scrollTop();
          this.$buefy.dialog.alert({
            title: "Error", message: "Hubo errores al asignar el rol.", type: "is-danger",
            hasIcon: true, icon: "times-circle", iconPack: "fa"
          });
          loading.close();
        });
      } else {
        axios.put(`/admin/person_units/${this.id}/update_role_fing.json`, {
          person_unit: this.person_unit,
          pu_relation_type_id: 2,
          person: personPayload
        }).then(() => {
          this.errores_validacion = {};
          this.$buefy.dialog.alert({ message: "El rol fue actualizado con éxito.", confirmText: "Ok" });
          loading.close();
          this.$emit("close");
        }).catch(err => {
          this.errores_validacion = err.response.data;
          this.scrollTop();
          this.$buefy.dialog.alert({
            title: "Error", message: "Hubo errores al actualizar el rol.", type: "is-danger",
            hasIcon: true, icon: "times-circle", iconPack: "fa"
          });
          loading.close();
        });
      }
    },
    scrollTop() {
      const el = document.getElementById("app");
      if (el) el.scrollIntoView();
    }
  },
  watch: {
    persona_seleccionada(val) {
      if (val) {
        this.person = {
          id: val.id,
          name: val.name,
          last_name: val.last_name,
          second_surname: val.second_surname,
          corporate_mail: val.corporate_mail,
          run_attributes: val.run_attributes || { rut: null, dv: null }
        };
      } else {
        this.clearPerson();
      }
    }
  }
};
</script>
